import { desembrulhar, fail, ok, type Result } from '../result.js';
import { baixarCaixa, ehInvisivel } from './texto.js';

/**
 * Review r9 segurança (B2): e-mail de IDENTIDADE (login, convite, membro, dono do tenant) só em ASCII. A parte local
 * aceita os caracteres visíveis do ASCII (sem espaço nem @); o domínio, letras, dígitos, hífen e ponto, com domínio
 * internacional (IDN) só na forma punycode (`xn--...`). Antes valia qualquer caractere fora do espaço, e dava para
 * convidar `dоna@...` (o cirílico) ou `dona\u200B@...`, visualmente iguais a um membro de verdade.
 */
const RE_EMAIL = /^[!#-?A-Z^-~]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
/** Domínio já em ASCII (o IDN convertido para punycode), para conferir o e-mail de contato. */
const RE_DOMINIO_ASCII = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
/** Parte local de contato: sem espaço, sem @, sem aspas nem barra invertida (o formato com aspas não é aceito). */
const RE_LOCAL_PROIBIDO = /[\s@"\\(),:;<>[\]]/u;

/**
 * Review r11 robustez: rótulo de domínio com até 63 caracteres e domínio inteiro com até 253 (RFC 1035). O parser de URL
 * do WHATWG não confere o comprimento (`verifyDnsLength` é falso), e `[a-z0-9-]*` não tem teto: `a@bbbb…b.com` com o
 * rótulo de 64 passava. Só vale para cadastro novo: `Email.reidratar` não valida, e o e-mail antigo segue legível.
 */
function comprimentoDeDnsOk(dominio: string): boolean {
  return dominio.length <= 253 && dominio.split('.').every((r) => r.length <= 63);
}

/** IDN para punycode (WHATWG URL, igual no Node e no navegador); null quando o domínio não é um host válido. */
function dominioAscii(dominio: string): string | null {
  try {
    const host = new URL(`http://${dominio}/`).hostname;
    return RE_DOMINIO_ASCII.test(host) ? host : null;
  } catch {
    return null;
  }
}

export class Email {
  private constructor(public readonly valor: string) {}

  /** E-mail de identidade (login, convite, membro): só ASCII, IDN em punycode. */
  static tryCreate(bruto: string): Result<Email> {
    const v = (bruto ?? '').trim().toLowerCase();
    if (!RE_EMAIL.test(v) || v.length > 254 || !comprimentoDeDnsOk(v.slice(v.lastIndexOf('@') + 1))) {
      return fail('email.invalido', 'E-mail inválido', { valor: bruto });
    }
    return ok(new Email(v));
  }

  static create(bruto: string): Email {
    return desembrulhar(Email.tryCreate(bruto));
  }

  /**
   * Review r10 spec (M1): e-mail de CONTATO (parceiro, contato, funcionário) segue a RFC 6531: parte local em UTF-8 e
   * domínio internacional (IDN), normalizados em NFC e em minúsculas, gravados como digitados (`joão@café.com.br`).
   * Recusa só controle e invisível (a classe de `ehInvisivel`), espaço, mais de um @, o formato com aspas e domínio
   * que não vira um host válido em punycode. A regra só de ASCII da r9 vale para identidade, onde o homóglifo é ataque;
   * no contato ela recusava e-mails reais e congelava o que já estava gravado.
   */
  static tryCreateContato(bruto: string): Result<Email> {
    const v = (bruto ?? '').trim().normalize('NFC').toLowerCase();
    const erro = () => fail<Email>('email.invalido', 'E-mail inválido', { valor: bruto });
    const arroba = v.lastIndexOf('@');
    if (arroba <= 0 || arroba !== v.indexOf('@') || v.length > 254) return erro();
    const local = v.slice(0, arroba);
    const dominio = v.slice(arroba + 1);
    // O que o parser de URL trataria como caminho, porta ou escape não é domínio (`a@b.com/x` viraria `b.com`).
    if (/[\s/?#:@\\%[\]]/u.test(dominio)) return erro();
    if (local.length > 64 || RE_LOCAL_PROIBIDO.test(local) || local.startsWith('.') || local.endsWith('.') || local.includes('..')) return erro();
    if (Array.from(v).some(ehInvisivel)) return erro();
    const ascii = dominioAscii(dominio);
    if (!ascii || !comprimentoDeDnsOk(ascii)) return erro();
    return ok(new Email(v));
  }

  /** Reconstrói o e-mail gravado no banco, SEM validar: regra nova nunca congela registro antigo (review r10 spec, M1). */
  static reidratar(gravado: string): Email {
    return new Email(gravado);
  }

  toString() {
    return this.valor;
  }
}

/**
 * Review r25 segurança (O1): o e-mail de contato com o domínio em ASCII, pelo mapeamento do IDNA (UTS 46, o mesmo do
 * WHATWG URL no Node e no navegador: NFKC, fullwidth, caixa e IDN em punycode). `fulano@ｄｏｍíｎｉｏ` vira
 * `fulano@xn--domnio-...`, o endereço que o servidor de e-mail entrega de fato. A parte local fica como veio (é da caixa
 * de destino). Null quando o domínio não vira um host válido. Para o e-mail que vira IDENTIDADE (convite do portal).
 */
export function emailComDominioAscii(email: string): string | null {
  const v = (email ?? '').trim();
  const arroba = v.lastIndexOf('@');
  if (arroba <= 0) return null;
  const dominio = v.slice(arroba + 1);
  if (/[\s/?#:@\\%[\]]/u.test(dominio)) return null;
  const ascii = dominioAscii(dominio);
  return ascii && comprimentoDeDnsOk(ascii) ? `${v.slice(0, arroba)}@${ascii}` : null;
}

/**
 * Rodada rrl-9 (ROB9-M1): a parte local só com ASCII visível. A validação de contato aceita `joão@empresa.com.br`, mas o Google
 * pode recusar esse endereço em `attendees` e recusar o evento inteiro; quem não passa aqui recebe só o e-mail do sistema.
 */
export const parteLocalAscii = (email: string) => /^[!-~]+$/.test(email.slice(0, email.lastIndexOf('@')));

const PUNY = { base: 36, tmin: 1, tmax: 26, skew: 38, damp: 700, bias: 72, n: 128 };

function adaptarPunycode(delta: number, pontos: number, primeiro: boolean): number {
  let d = primeiro ? Math.floor(delta / PUNY.damp) : Math.floor(delta / 2);
  d += Math.floor(d / pontos);
  let k = 0;
  while (d > ((PUNY.base - PUNY.tmin) * PUNY.tmax) >> 1) {
    d = Math.floor(d / (PUNY.base - PUNY.tmin));
    k += PUNY.base;
  }
  return k + Math.floor(((PUNY.base - PUNY.tmin + 1) * d) / (d + PUNY.skew));
}

/**
 * Decodificação punycode (RFC 3492) de um rótulo sem o `xn--`; null quando inválido. A mesma conta da função SQL
 * `cadastros_punycode_decodificar` (cadastros 0006), para a chave canônica dar igual nos dois lados.
 */
export function decodificarPunycode(entrada: string): string | null {
  let n = PUNY.n;
  let i = 0;
  let bias = PUNY.bias;
  const b = entrada.lastIndexOf('-');
  const saida: number[] = [];
  for (const ch of b > 0 ? entrada.slice(0, b) : '') {
    const c = ch.codePointAt(0)!;
    if (c >= 0x80) return null;
    saida.push(c);
  }
  let pos = b > 0 ? b + 1 : 0;
  while (pos < entrada.length) {
    const antigo = i;
    let w = 1;
    for (let k = PUNY.base; ; k += PUNY.base) {
      if (pos >= entrada.length) return null;
      const c = entrada.charCodeAt(pos++);
      const d = c >= 0x30 && c <= 0x39 ? c - 22 : c >= 0x61 && c <= 0x7a ? c - 0x61 : c >= 0x41 && c <= 0x5a ? c - 0x41 : -1;
      if (d < 0) return null;
      i += d * w;
      if (i > 0x7fffffff) return null;
      const t = k <= bias ? PUNY.tmin : k >= bias + PUNY.tmax ? PUNY.tmax : k - bias;
      if (d < t) break;
      w *= PUNY.base - t;
      if (w > 0x7fffffff) return null;
    }
    const total = saida.length + 1;
    bias = adaptarPunycode(i - antigo, total, antigo === 0);
    n += Math.floor(i / total);
    i %= total;
    if (n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return null;
    saida.splice(i, 0, n);
    i++;
  }
  return String.fromCodePoint(...saida);
}

/**
 * Review r11 spec (B4): chave canônica do e-mail de contato, para casar o mesmo endereço gravado em outra forma (NFD,
 * maiúscula fora do ASCII, domínio em punycode de um lado e em Unicode do outro). Parte local em NFC e minúsculas;
 * domínio com cada rótulo `xn--` decodificado, em NFKC e minúsculas. A caixa é por tabela (`baixarCaixa`). A mesma
 * conta da função SQL `cadastros_email_chave` (coluna gerada `cadastros_parceiros.email_chave`, cadastros 0006).
 */
export function chaveDeEmail(email: string): string {
  const v = baixarCaixa(email.replace(/^ +| +$/g, '').normalize('NFC'));
  const arroba = v.lastIndexOf('@');
  if (arroba < 0) return v;
  const dominio = v
    .slice(arroba + 1)
    .split('.')
    .map((r) => (r.startsWith('xn--') ? (decodificarPunycode(r.slice(4)) ?? r) : r))
    .join('.');
  return `${v.slice(0, arroba)}@${baixarCaixa(dominio.normalize('NFKC'))}`;
}
